/**
 * Usercentrics "Google Ads Remarketing" consent detection.
 *
 * BFS runs the newer Web CMP (`window.__ucCmp`, loader `web.cmp.usercentrics.eu/ui`) while
 * BFSS runs the classic Browser SDK (`window.UC_UI`, loader `app.usercentrics.eu/browser-ui`).
 * The two products expose different APIs, so this module is the single place that knows how to
 * read consent for either one. Everything else in the app should depend only on
 * `getGoogleAdsConsent()` / `onGoogleAdsConsentChange()`.
 */

type BrowserSdkService = {
  id: string;
  name: string;
  consent?: { status?: boolean };
};

type WebCmpService = {
  name?: string;
  consent?: { given?: boolean };
};

type WebCmpConsentDetails = {
  services?: Record<string, WebCmpService>;
};

declare global {
  interface Window {
    // Classic Browser SDK (BFSS)
    UC_UI?: {
      isInitialized?: () => boolean;
      getServicesBaseInfo?: () => BrowserSdkService[];
    };
    // Web CMP (BFS)
    __ucCmp?: {
      getConsentDetails?: () => Promise<WebCmpConsentDetails>;
    };
  }
}

/**
 * Human-readable Usercentrics service name for Google Ads Remarketing. Matching by name works
 * for both CMP products. Kept as a single constant so it can be swapped for a template id if the
 * live BFS configuration requires it.
 */
export const GOOGLE_ADS_SERVICE_NAME = 'Google Ads Remarketing';

/**
 * Window events emitted by Usercentrics when consent changes. The exact set differs between the
 * Web CMP and the Browser SDK and across versions, so we listen to all known candidates and
 * simply re-derive consent whenever any of them fires. Confirm/trim against the live BFS config.
 */
const USERCENTRICS_CONSENT_EVENTS = [
  'UC_UI_CMP_EVENT',
  'UC_UI_INITIALIZED',
  'ucEvent',
  'onConsentStatusChange',
] as const;

/**
 * Last known Google Ads consent value. Populated synchronously from the Browser SDK when
 * available and asynchronously from the Web CMP (whose API is Promise-based) via
 * `refreshWebCmpConsentCache`. `undefined` means "unknown / not yet determined".
 */
let cachedConsent: boolean | undefined;

const findServiceConsent = (name: string): boolean | undefined => {
  // Browser SDK (BFSS): synchronous.
  const uc = typeof window !== 'undefined' ? window.UC_UI : undefined;
  if (uc?.isInitialized?.() && uc.getServicesBaseInfo) {
    const service = uc.getServicesBaseInfo().find((s) => s.name === name);
    if (service) {
      return Boolean(service.consent?.status);
    }
  }
  return undefined;
};

/** Refreshes the cache from the Web CMP (BFS), whose consent API is asynchronous. */
const refreshWebCmpConsentCache = async (): Promise<boolean | undefined> => {
  const cmp = typeof window !== 'undefined' ? window.__ucCmp : undefined;
  if (!cmp?.getConsentDetails) {
    return undefined;
  }
  try {
    const details = await cmp.getConsentDetails();
    const service = Object.values(details?.services ?? {}).find(
      (s) => s?.name === GOOGLE_ADS_SERVICE_NAME,
    );
    if (service) {
      cachedConsent = Boolean(service.consent?.given);
      return cachedConsent;
    }
  } catch {
    // Ignore – treated as "unknown".
  }
  return undefined;
};

/**
 * Synchronous best-effort read of Google Ads Remarketing consent.
 * Returns `true`, `false`, or `undefined` when consent cannot be determined yet.
 */
export const getGoogleAdsConsent = (): boolean | undefined => {
  const sdkConsent = findServiceConsent(GOOGLE_ADS_SERVICE_NAME);
  if (sdkConsent !== undefined) {
    cachedConsent = sdkConsent;
    return sdkConsent;
  }
  return cachedConsent;
};

/**
 * Subscribes to Usercentrics consent changes. Invokes `callback` with the current Google Ads
 * consent boolean whenever it can be (re-)determined. Returns an unsubscribe function.
 */
export const onGoogleAdsConsentChange = (callback: (granted: boolean) => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handle = () => {
    const sync = findServiceConsent(GOOGLE_ADS_SERVICE_NAME);
    if (sync !== undefined) {
      cachedConsent = sync;
      callback(sync);
      return;
    }
    // Web CMP: async read, then notify.
    void refreshWebCmpConsentCache().then((consent) => {
      if (consent !== undefined) {
        callback(consent);
      }
    });
  };

  USERCENTRICS_CONSENT_EVENTS.forEach((event) => window.addEventListener(event, handle));

  // Attempt an immediate read in case consent is already available (e.g. returning visitor).
  handle();

  return () => {
    USERCENTRICS_CONSENT_EVENTS.forEach((event) => window.removeEventListener(event, handle));
  };
};
